import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import useYamlData from '../hooks/useYamlData';
import BootLog from '../components/BootLog';
import '../styles/Homelab.css';

/*
 * The homelab as an operator console rather than four grids of cards.
 *
 * Everything here is read out of homelab.yaml, and there is no telemetry behind
 * that file -- no load average, no uptime, no health check. So the console
 * reports inventory and capacity only, and every meter is a share of the
 * fleet's own maximum. A terminal that invented a CPU graph would look better
 * and mean nothing, and a visitor has no way to tell a real number on this page
 * from a decorative one.
 */

// "64GB" / "1TB SSD" / "256GB Flash drive" -> gigabytes.
const gigabytes = (text) => {
  const match = /([\d.]+)\s*(TB|GB|MB)/i.exec(String(text ?? ''));
  if (!match) return 0;
  const value = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  if (unit === 'TB') return value * 1024;
  if (unit === 'MB') return value / 1024;
  return value;
};

const capacity = (gb) =>
  gb >= 1024 ? `${Number((gb / 1024).toFixed(1))} TB` : `${Math.round(gb)} GB`;

const COMMANDS = [
  ['all', 'show every pane'],
  ['net', 'network devices'],
  ['hosts', 'host inventory'],
  ['clusters', 'cluster membership'],
  ['services', 'hosted services'],
  ['help', 'this list'],
  ['clear', 'reset the query'],
];
const COMMAND_NAMES = COMMANDS.map(([name]) => name);

/*
 * Network gear is grouped by the role it plays, not wired into a topology. The
 * data records what each device is, never what it is plugged into, so any cable
 * drawn between two of these boxes would be invented.
 */
const NET_TIERS = [
  { key: 'uplink', label: 'Uplink', accent: 'var(--con-cyan)', types: ['cable', 'dsl', 'fiber'] },
  { key: 'edge', label: 'Edge', accent: 'var(--con-amber)', types: ['firewall', 'router'] },
  { key: 'switching', label: 'Switching', accent: 'var(--con-green)', types: ['switch'] },
  { key: 'wireless', label: 'Wireless', accent: 'var(--con-violet)', types: ['wifi', 'satellite'] },
];

// One accent per cluster, so a cluster is the same colour wherever it appears.
const CLUSTER_ACCENTS = [
  'var(--con-green)',
  'var(--con-cyan)',
  'var(--con-amber)',
  'var(--con-violet)',
];

const haystack = (...parts) => parts.filter(Boolean).join(' ').toLowerCase();

const Homelab = () => {
  const { data, loading, error } = useYamlData('/data/homelab.yaml');
  const [query, setQuery] = useState('');
  const [view, setView] = useState('all');
  const [openHost, setOpenHost] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef(null);

  // "/" focuses the prompt from anywhere on the page, the way a pager does.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== '/' || event.target.tagName === 'INPUT') return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const { servers, networking, clusters, services } = useMemo(
    () => ({
      servers: data?.hardware?.servers ?? [],
      networking: data?.hardware?.networking ?? [],
      clusters: data?.clusters ?? [],
      services: data?.services ?? [],
    }),
    [data],
  );

  // Clusters reference servers by id; a host may belong to none.
  const clusterOf = useMemo(() => {
    const map = new Map();
    for (const cluster of clusters) {
      for (const node of cluster.nodes ?? []) map.set(node, cluster.name);
    }
    return map;
  }, [clusters]);

  const serverName = (id) => servers.find((server) => server.id === id)?.name ?? id;

  const fleet = useMemo(() => {
    const sum = (pick) => servers.reduce((total, server) => total + pick(server), 0);
    return {
      cores: sum((server) => server.spec?.cpu ?? 0),
      memory: sum((server) => gigabytes(server.spec?.memory)),
      storage: sum((server) =>
        (server.spec?.storage ?? []).reduce((total, disk) => total + gigabytes(disk), 0),
      ),
    };
  }, [servers]);

  // Meters are drawn against the biggest host, so the bars compare like to like.
  const peak = useMemo(
    () => ({
      cores: Math.max(1, ...servers.map((server) => server.spec?.cpu ?? 0)),
      memory: Math.max(1, ...servers.map((server) => gigabytes(server.spec?.memory))),
    }),
    [servers],
  );

  const q = query.trim().toLowerCase();

  const found = useMemo(() => {
    const hosts = servers.filter((server) =>
      haystack(
        server.name,
        server.id,
        server.type,
        server.description,
        server.spec?.cputype,
        server.spec?.memory,
        clusterOf.get(server.id),
      ).includes(q),
    );
    return {
      hosts,
      net: networking.filter((device) =>
        haystack(device.name, device.type, device.description).includes(q),
      ),
      clusters: clusters.filter((cluster) =>
        haystack(
          cluster.name,
          cluster.description,
          ...(cluster.nodes ?? []).map(serverName),
        ).includes(q),
      ),
      services: services.filter((service) =>
        haystack(service.name, service.type, service.description).includes(q),
      ),
    };
    // serverName reads servers, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, servers, networking, clusters, services, clusterOf]);

  // Services carry no host in the data, so they group by what they do instead.
  const serviceGroups = useMemo(() => {
    const groups = new Map();
    for (const service of found.services) {
      const key = service.type ?? 'other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(service);
    }
    return [...groups.entries()].sort(
      (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
    );
  }, [found.services]);

  /*
   * Everything a cluster card draws: its members resolved, its capacity totalled
   * and its accent fixed by position in the file, so filtering the list cannot
   * recolour a cluster.
   */
  const clusterMeta = useMemo(() => {
    const meta = new Map();
    clusters.forEach((cluster, index) => {
      const members = (cluster.nodes ?? [])
        .map((id) => servers.find((server) => server.id === id))
        .filter(Boolean);
      meta.set(cluster.name, {
        accent: CLUSTER_ACCENTS[index % CLUSTER_ACCENTS.length],
        members,
        cores: members.reduce((total, s) => total + (s.spec?.cpu ?? 0), 0),
        memory: members.reduce((total, s) => total + gigabytes(s.spec?.memory), 0),
      });
    });
    return meta;
  }, [clusters, servers]);

  // Cluster meters compare clusters to each other, not to the whole fleet.
  const clusterPeak = useMemo(() => {
    const all = [...clusterMeta.values()];
    return {
      cores: Math.max(1, ...all.map((entry) => entry.cores)),
      memory: Math.max(1, ...all.map((entry) => entry.memory)),
      nodeCores: Math.max(1, ...all.flatMap((e) => e.members.map((m) => m.spec?.cpu ?? 0))),
    };
  }, [clusterMeta]);

  const netTiers = useMemo(
    () =>
      NET_TIERS.map((tier) => ({
        ...tier,
        devices: found.net.filter((device) => tier.types.includes(device.type)),
      })).filter((tier) => tier.devices.length),
    [found.net],
  );

  // Anything whose type falls outside the known roles still has to appear.
  const netOther = useMemo(
    () => found.net.filter((device) => !NET_TIERS.some((tier) => tier.types.includes(device.type))),
    [found.net],
  );

  /*
   * Tiers and the leftovers as one ordered list. The stages have to be numbered
   * for the tap pulses to fire in step with the packets, and that number cannot
   * be known while they are rendered from three separate blocks.
   */
  const netStages = useMemo(() => {
    const stages = [...netTiers];
    if (netOther.length) {
      stages.push({ key: 'other', label: 'Other', accent: 'var(--con-dim)', devices: netOther });
    }
    return stages;
  }, [netTiers, netOther]);

  const bootLines = useMemo(
    () => [
      { key: 'net', value: `${networking.length} devices` },
      { key: 'hosts', value: `${servers.length} registered` },
      { key: 'cores', value: `${fleet.cores} total` },
      { key: 'memory', value: capacity(fleet.memory) },
      { key: 'storage', value: capacity(fleet.storage) },
      { key: 'clusters', value: `${clusters.length} defined` },
      { key: 'services', value: `${services.length} catalogued` },
    ],
    [networking.length, servers.length, clusters.length, services.length, fleet],
  );

  if (loading) return <p className="tmc-state">Loading homelab data...</p>;
  if (error) return <p className="tmc-state tmc-state--error">{error}</p>;

  const submit = (event) => {
    event.preventDefault();
    const word = q.split(/\s+/)[0];
    if (!COMMAND_NAMES.includes(word)) return; // free text stays a live filter
    setQuery('');
    setShowHelp(word === 'help');
    if (word === 'clear') setView('all');
    else if (word !== 'help') setView(word);
  };

  const onPromptKey = (event) => {
    if (event.key !== 'Escape') return;
    setQuery('');
    setShowHelp(false);
  };

  const isCommand = COMMAND_NAMES.includes(q);
  const visible = (pane) => view === 'all' || view === pane;
  const nothing =
    !found.hosts.length && !found.net.length && !found.clusters.length && !found.services.length;

  const meter = (value, max) => ({ '--fill': `${Math.max(3, (value / max) * 100)}%` });

  return (
    <div className="tmc-page page-shell homelab-page">
      <header className="tmc-page-header">
        <p className="eyebrow">Homelab</p>
        <h1>The rack behind the writing.</h1>
        <p className="tmc-lede">
          Current hardware, clusters, and self-hosted services, the environment I use to
          keep hands-on with the systems I lead. This is a point-in-time snapshot, not a
          live link into the rack -- filter it below.
        </p>
      </header>

      <div className="console">
        <div className="console-bar">
          <span className="console-lights" aria-hidden="true">
            <i /><i /><i />
          </span>
          <span className="console-title">tmc-homelab — inventory</span>
          {/* No socket behind this console: everything below reads from
              homelab.yaml at page load, never a live host. */}
          <span className="console-badge" title="Static data from homelab.yaml, not a live connection">
            snapshot
          </span>
          <span className="console-scope">
            {view === 'all' ? 'all panes' : view}
            {q ? ` · "${q}"` : ''}
          </span>
        </div>

        <div className="console-body">
          <BootLog lines={bootLines} />

          <dl className="fleet">
            <div className="fleet-stat">
              <dt>Hosts</dt>
              <dd>{servers.length}</dd>
            </div>
            <div className="fleet-stat">
              <dt>Cores</dt>
              <dd>{fleet.cores}</dd>
            </div>
            <div className="fleet-stat">
              <dt>Memory</dt>
              <dd>{capacity(fleet.memory)}</dd>
            </div>
            <div className="fleet-stat">
              <dt>Storage</dt>
              <dd>{capacity(fleet.storage)}</dd>
            </div>
            <div className="fleet-stat">
              <dt>Clusters</dt>
              <dd>{clusters.length}</dd>
            </div>
            <div className="fleet-stat">
              <dt>Services</dt>
              <dd>{services.length}</dd>
            </div>
          </dl>

          <form className="prompt" onSubmit={submit} role="search">
            {/* Decoration: the input carries its own name, so the sigil must not become it. */}
            <span className="prompt-sign" aria-hidden="true">&gt;</span>
            <input
              id="homelab-query"
              ref={inputRef}
              className="prompt-input"
              type="search"
              aria-label="Filter hosts, clusters and services"
              value={query}
              autoComplete="off"
              spellCheck="false"
              placeholder="filter hosts, clusters and services — or type help"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onPromptKey}
            />
            {isCommand && <span className="prompt-hint">press enter to run</span>}
          </form>

          <p className="console-keys">
            <kbd>/</kbd> focus <span aria-hidden="true">·</span> <kbd>esc</kbd> clear{' '}
            <span aria-hidden="true">·</span> <kbd>help</kbd> commands
          </p>

          {showHelp && (
            <dl className="help-list">
              {COMMANDS.map(([name, description]) => (
                <div className="help-row" key={name}>
                  <dt>{name}</dt>
                  <dd>{description}</dd>
                </div>
              ))}
            </dl>
          )}

          {nothing ? (
            <p className="console-empty">
              no match for <strong>{q}</strong> — <button type="button" onClick={() => setQuery('')}>reset</button>
            </p>
          ) : (
            <>
              {visible('net') && netStages.length > 0 && (
                <section className="pane" aria-labelledby="net-heading">
                  <div className="pane-head">
                    <h2 id="net-heading">net</h2>
                    <span className="pane-count">{found.net.length} devices</span>
                  </div>

                  {/*
                    * --taps is every stage plus the endcap; each stage's --tap is
                    * where its column centre falls along that run. The pulses and
                    * the packets share one cycle, so a tap lights as the flow
                    * reaches it rather than on a timer of its own.
                    */}
                  <div
                    className="net-map"
                    style={{ '--taps': netStages.length + 1 }}
                  >
                    <span className="net-flow" aria-hidden="true">
                      <i />
                      <i />
                      <i />
                    </span>

                    <ol className="net-rail">
                      {netStages.map((stage, index) => (
                        <li
                          className="net-stage"
                          key={stage.key}
                          style={{ '--accent': stage.accent, '--tap': index }}
                        >
                          <p className="net-stage-label">
                            <span className="net-stage-dot" aria-hidden="true" />
                            {stage.label}
                          </p>
                          <ul className="net-list">
                            {stage.devices.map((device) => (
                              <li className="net-node" key={device.name}>
                                <strong>{device.name}</strong>
                                <span className="net-type">{device.type}</span>
                                <span className="net-desc">{device.description}</span>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}

                      <li
                        className="net-stage net-stage--end"
                        style={{ '--accent': 'var(--con-ink)', '--tap': netStages.length }}
                      >
                        <p className="net-stage-label">
                          <span className="net-stage-dot" aria-hidden="true" />
                          Hosts
                        </p>
                        <button type="button" className="net-endcap" onClick={() => setView('hosts')}>
                          <strong>{servers.length}</strong>
                          <span>machines</span>
                        </button>
                      </li>
                    </ol>
                  </div>
                </section>
              )}

              {visible('hosts') && found.hosts.length > 0 && (
                <section className="pane" aria-labelledby="hosts-heading">
                  <div className="pane-head">
                    <h2 id="hosts-heading">hosts</h2>
                    <span className="pane-count">
                      {found.hosts.length} of {servers.length}
                    </span>
                  </div>
                  <ul className="host-list">
                    <li className="host-row host-row--head" aria-hidden="true">
                      <span>host</span>
                      <span>cluster</span>
                      <span>cores</span>
                      <span>memory</span>
                      <span>storage</span>
                    </li>
                    {found.hosts.map((server) => {
                      const store = (server.spec?.storage ?? []).reduce(
                        (total, disk) => total + gigabytes(disk),
                        0,
                      );
                      const open = openHost === server.id;
                      return (
                        <li className={`host-item${open ? ' is-open' : ''}`} key={server.id}>
                          <button
                            type="button"
                            className="host-row"
                            aria-expanded={open}
                            onClick={() => setOpenHost(open ? null : server.id)}
                          >
                            <span className="host-name">
                              <span className="host-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                              {server.name}
                              <em className="host-type">{server.type}</em>
                            </span>
                            <span
                              className="host-cluster"
                              style={{ '--accent': clusterMeta.get(clusterOf.get(server.id))?.accent }}
                            >
                              {clusterOf.has(server.id) && <i className="host-cluster-dot" aria-hidden="true" />}
                              {clusterOf.get(server.id) ?? '—'}
                            </span>
                            <span className="host-metric">
                              <span className="host-meter" style={meter(server.spec?.cpu ?? 0, peak.cores)} aria-hidden="true" />
                              {server.spec?.cpu}c
                            </span>
                            <span className="host-metric">
                              <span className="host-meter" style={meter(gigabytes(server.spec?.memory), peak.memory)} aria-hidden="true" />
                              {server.spec?.memory}
                            </span>
                            <span className="host-storage">{capacity(store)}</span>
                          </button>
                          {open && (
                            <div className="host-detail">
                              <p>{server.description}</p>
                              <dl className="host-specs">
                                {server.spec?.cputype && (
                                  <div>
                                    <dt>CPU</dt>
                                    <dd>{server.spec.cputype}</dd>
                                  </div>
                                )}
                                {server.spec?.gpu && (
                                  <div>
                                    <dt>GPU</dt>
                                    <dd>{server.spec.gpu}</dd>
                                  </div>
                                )}
                                <div>
                                  <dt>Disks</dt>
                                  <dd>{(server.spec?.storage ?? []).join(', ')}</dd>
                                </div>
                                <div>
                                  <dt>Id</dt>
                                  <dd>{server.id}</dd>
                                </div>
                              </dl>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {visible('clusters') && found.clusters.length > 0 && (
                <section className="pane" aria-labelledby="clusters-heading">
                  <div className="pane-head">
                    <h2 id="clusters-heading">clusters</h2>
                    <span className="pane-count">{found.clusters.length} defined</span>
                  </div>
                  <div className="cluster-grid">
                    {found.clusters.map((cluster) => {
                      const meta = clusterMeta.get(cluster.name);
                      const nodes = cluster.nodes ?? [];
                      return (
                        <article className="cluster" key={cluster.name} style={{ '--accent': meta.accent }}>
                          <div className="cluster-head">
                            <span className="cluster-dot" aria-hidden="true" />
                            <h3>{cluster.name}</h3>
                            <span className="cluster-size">
                              {nodes.length}
                              {nodes.length === 1 ? ' node' : ' nodes'}
                            </span>
                          </div>

                          {/*
                            * The members as a row of bodies, each sized by its
                            * share of the biggest node in any cluster. Three
                            * equal Dells look equal; one Pi beside a workstation
                            * does not -- which a list of names cannot show.
                            */}
                          <ul className="node-row">
                            {meta.members.map((member) => (
                              <li key={member.id}>
                                <button
                                  type="button"
                                  className="node"
                                  onClick={() => setQuery(member.name)}
                                >
                                  <span
                                    className="node-glyph"
                                    aria-hidden="true"
                                    style={{
                                      '--size': `${
                                        11 + Math.sqrt((member.spec?.cpu ?? 0) / clusterPeak.nodeCores) * 15
                                      }px`,
                                    }}
                                  />
                                  <span className="node-label">{member.name}</span>
                                </button>
                              </li>
                            ))}
                          </ul>

                          <dl className="cluster-meters">
                            <div>
                              <dt>cores</dt>
                              <dd>
                                <span
                                  className="cluster-meter"
                                  aria-hidden="true"
                                  style={meter(meta.cores, clusterPeak.cores)}
                                />
                                {meta.cores}
                              </dd>
                            </div>
                            <div>
                              <dt>memory</dt>
                              <dd>
                                <span
                                  className="cluster-meter"
                                  aria-hidden="true"
                                  style={meter(meta.memory, clusterPeak.memory)}
                                />
                                {capacity(meta.memory)}
                              </dd>
                            </div>
                          </dl>

                          <p className="cluster-desc">{cluster.description}</p>
                        </article>
                      );
                    })}
                  </div>
                </section>
              )}

              {visible('services') && serviceGroups.length > 0 && (
                <section className="pane" aria-labelledby="services-heading">
                  <div className="pane-head">
                    <h2 id="services-heading">services</h2>
                    <span className="pane-count">
                      {found.services.length} across {serviceGroups.length} roles
                    </span>
                  </div>
                  <div className="svc-groups">
                    {serviceGroups.map(([type, items]) => (
                      <div className="svc-group" key={type}>
                        <p className="svc-group-head">
                          <button type="button" onClick={() => setQuery(type)}>
                            {type.replace(/-/g, ' ')}
                          </button>
                          <span className="svc-group-count">{items.length}</span>
                        </p>
                        <ul className="svc-list">
                          {items.map((service) => (
                            <li className="svc" key={service.name}>
                              <a href={service.project} target="_blank" rel="noopener noreferrer">
                                {service.name}
                                <ExternalLink size={12} aria-hidden="true" />
                              </a>
                              <span className="svc-desc">{service.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          <p className="console-note">
            Inventory and capacity read from <code>homelab.yaml</code>. No live telemetry:
            the meters compare each host against the largest in the fleet, and nothing on
            this page reports status.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Homelab;
